import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Product, ProductConfig } from '../../types/product';
import { useGetProductVariantsQuery, useSaveProductConfigMutation } from '../../api/productDisplayApi';
import { ProductConfigurator } from './ProductConfigurator';

// Define a more specific type for our component's state
interface ProductConfigState extends ProductConfig {
  dimensions: Required<ProductConfig['dimensions']>
}

interface ProductDisplayProps {
  product: Product;
  onConfigure?: (config: ProductConfig) => void;
}

export const ProductDisplay: React.FC<ProductDisplayProps> = ({ product, onConfigure }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: productData, isLoading } = useGetProductVariantsQuery(product.id);
  const [saveProductConfig, { isSuccess }] = useSaveProductConfigMutation();
  const [config, setConfig] = useState<ProductConfig>({
    type: 'fabric',
    material: '',
    color: '',
    size: '',
    quantity: 1,
    customization: '',
    dimensions: { width: 0, height: 0, depth: 0 },
    specialRequests: []
  });
  const [openConfigDialog, setOpenConfigDialog] = useState(false);

  useEffect(() => {
    if (productData?.[0]) {
      const variant = productData[0];
      setConfig({
        type: variant.type || 'fabric',
        material: variant.material,
        color: variant.color,
        size: variant.size,
        quantity: 1,
        customization: '',
        dimensions: { width: 0, height: 0, depth: 0 },
        specialRequests: []
      });
    }
  }, [productData, setConfig]);

  const handleConfigure = () => {
    setOpenConfigDialog(true);
  };

  const handleConfigSubmit = async (newConfig: ProductConfig) => {
    try {
      await saveProductConfig({ productId: product.id, config: newConfig }).unwrap();
      if (onConfigure) {
        onConfigure(newConfig);
      }
      setOpenConfigDialog(false);
    } catch (error) {
      console.error('Failed to save configuration:', error);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading product details...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 3,
        }}
      >
        <CardMedia
          component="img"
          height="300"
          image={(productData?.[0]?.images?.[0] as string) || '/default-product.png'}
          alt={product.name}
          sx={{
            objectFit: 'contain',
            width: '100%',
            height: 'auto',
            mb: 3,
          }}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography variant="h5" gutterBottom>
            {product.name}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" paragraph>
            {product.description}
          </Typography>
          <Typography variant="h6" color="primary" paragraph>
            KES {product.price.toLocaleString()}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfigure}
            fullWidth
          >
            Configure Product
          </Button>
        </CardContent>

        <Dialog
          open={openConfigDialog}
          onClose={() => setOpenConfigDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Configure {product.name}</DialogTitle>
          <DialogContent>
            {/* Product configurator component */}
            <ProductConfigurator
              product={product}
              config={config}
              onSave={(newConfig) => handleConfigSubmit(newConfig)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfigDialog(false)}>Cancel</Button>
            <Button
              onClick={() => handleConfigSubmit(config)}
              variant="contained"
              color="primary"
            >
              Save Configuration
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Box>
  );
};
